/**
 * HISTORY - THE ARTICLE'S STORED VERSIONS, READ-ONLY, AND "KEEP THIS VERSION" (register R-017,
 * lens findings C2-06 and U1-05).
 *
 * The three version operations were served and nothing in this app called them, so an author could
 * not see an earlier version or keep one on purpose; the editor said "Revision N" and nothing else.
 * PRO-212 rules "storage + minimal UI" (00-scope-v1.md), and this is the minimal UI: the list,
 * newest first; one version opened read-only, in the article's own column (below); and a button
 * that keeps the draft as a new version.
 *
 * ★ THERE IS NO RESTORE, AND NOTHING HERE PRETENDS OTHERWISE. "There is deliberately no restore
 * route" (authors-api.yaml, `listArticleVersions`; 09-authors-app.md, "that was the v1 cut"). An
 * author who wants an old paragraph back copies it from the opened version.
 *
 * ★ "KEEP THIS VERSION" SAVES FIRST, AS SEND DOES. `saveArticleVersion` takes no body and seals the
 * working copy the autosave has written - so pressed inside the autosave's pause it would keep the
 * copy one pause behind the screen (U1-05's attack). `saveFirst` is the editor's `saveNow`: it
 * waits for a save in the air, sends the newest draft, and answers false when that did not land -
 * then nothing is kept, and the conflict pane or the "not saved" alert already says why.
 *
 * ★ A KEPT VERSION FIXES THE PIECE'S LANGUAGE: the platform freezes `sourceLanguage` from the first
 * stored version on (`applySourceLanguage`), so the button says so before it is pressed.
 *
 * ★ A VERSION IS READ IN THE ARTICLE'S OWN COLUMN (design 24, T20; the owner's pick). "Read" used
 * to draw it below the whole list, in this narrow column, where nobody found it on an article with
 * fourteen translations. So this panel only reports the pick (`reading`, `onRead`) and the screen
 * draws `VersionInColumn` where the article is: in the editor over the draft, which is only hidden
 * and keeps everything typed (../EditorRoute.tsx), and in the read view in place of the article
 * (./ArticleReadView.tsx). It is drawn as the read view draws an article - text and quotes through
 * ./ReadOnlyText.tsx, so headings, bold and links show; pictures with their captions; dividers. The
 * row being read says "Reading" and its button goes "Back". The editor offers no "Read" while an
 * image is uploading (`canRead`).
 */
import { Alert, Button, Group, Stack, Text, Title } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditorApi } from '../../../features/cabinet/api/editor-api';
import { describeProblem, hasErrorCode } from '../../../features/cabinet/api/problem';
import { ErrorState, LoadingState } from '../../../features/cabinet/components';
import { formatDay, formatInstant } from '../../../features/cabinet/format';
import { useAction, useAsync } from '../../../features/cabinet/state';
import { emulatorImageUrl, servedUrlOnError } from '../../../features/storage/emulator-image-url';
import { useTranslation } from '../../../i18n';
import type { ArticlesT } from '../articles-i18n';
import { BlockVideoPlayer } from './VideoBlockPlayer';
import { ReadOnlyText } from './ReadOnlyText';
import { sourceLanguageLabel } from './document';
import editorClasses from './editor.module.css';
import classes from './read-view.module.css';
import type {
  ArticleVersionSummary,
  Block,
  VersionList,
} from '../../../features/cabinet/api/editor-api';
import type { AsyncResult } from '../../../features/cabinet/state';

export interface HistoryPanelProps {
  readonly articleId: string;
  /** `listArticleVersions`, read once by the editor: the language picker reads it too. */
  readonly versions: AsyncResult<VersionList>;
  /** False while the article is not its author's to write - the platform refuses a version then. */
  readonly canKeep: boolean;
  /** Saves the draft now; false when that save did not land. The editor's `saveNow`. */
  readonly saveFirst: () => Promise<boolean>;
  /** The version open in the article's column, if any. The screen holds it and draws it there. */
  readonly reading: OpenedVersion | undefined;
  /** Opens a version in the article's column, or goes back with `undefined`. */
  readonly onRead: (version: OpenedVersion | undefined) => void;
  /** False while an image is uploading: the editor offers no "Read" then. */
  readonly canRead?: boolean;
}

/**
 * What made each version, in the author's words (`editor.history.reasons`). An imported version is
 * the piece as it first stood here, so it is the "Original version" - never where it came from
 * (design 15, the owner's plain-words rule: "do we really need to show internal tech info like `in
 * the old apps`").
 */
function reasonLabel(reason: ArticleVersionSummary['reason'], t: ArticlesT): string {
  return t(`editor.history.reasons.${reason}`);
}

/** One version's address: its number, and a language only for a translation row. */
export interface OpenedVersion {
  readonly revisionNo: number;
  readonly language: string | undefined;
}

/** What the last press of "Keep this version" came to. */
type KeepOutcome =
  { readonly kind: 'kept'; readonly revisionNo: number } | { readonly kind: 'not-saved' };

export function HistoryPanel({
  articleId,
  versions,
  canKeep,
  saveFirst,
  reading,
  onRead,
  canRead = true,
}: HistoryPanelProps) {
  const { t } = useTranslation('articles');
  const api = useEditorApi();
  const [outcome, setOutcome] = useState<KeepOutcome | undefined>(undefined);

  const keep = useAction(async () => {
    const saved = await saveFirst();
    if (!saved) {
      return null;
    }
    return await api.saveArticleVersion(articleId);
  });

  const onKeep = () => {
    setOutcome(undefined);
    void (async () => {
      const kept = await keep.run();
      if (kept === null) {
        setOutcome({ kind: 'not-saved' });
      } else if (kept !== undefined) {
        setOutcome({ kind: 'kept', revisionNo: kept.revisionNo });
        versions.refetch();
      }
    })();
  };

  return (
    <Stack component="section" gap="md" aria-labelledby="editor-history-title">
      <Stack gap={4}>
        <Title id="editor-history-title" order={2} size="h5">
          {t('editor.history.title')}
        </Title>
        <Text size="xs" c="dimmed">
          {canKeep ? t('editor.history.intro') : t('editor.history.introReadOnly')}
        </Text>
      </Stack>

      {/* NOTHING TO KEEP WHERE THE AUTHOR CANNOT WRITE: the read view of a published or in-review
          piece (./ArticleReadView.tsx) lists the versions and offers no control it would refuse. */}
      {canKeep ? (
        <Stack gap={6}>
          <Group gap="sm" wrap="wrap">
            <Button
              size="xs"
              variant="default"
              disabled={!canKeep}
              loading={keep.isRunning}
              aria-describedby="editor-keep-version-note"
              onClick={onKeep}
            >
              {t('editor.history.keep')}
            </Button>
          </Group>
          <Text id="editor-keep-version-note" size="xs" c="dimmed">
            {canKeep ? t('editor.history.keepNote') : t('editor.history.keepNoteReadOnly')}
          </Text>
          {/* ALWAYS MOUNTED, EVEN EMPTY: a live region that appears with its message may not be read. */}
          <Text role="status" size="xs">
            {outcome === undefined
              ? ''
              : outcome.kind === 'kept'
                ? t('editor.history.kept', { number: outcome.revisionNo })
                : t('editor.history.notKept')}
          </Text>
          {keep.error === undefined ? null : (
            <Alert
              variant="light"
              role="alert"
              title={t('editor.history.keepFailed')}
              withCloseButton
              closeButtonLabel={t('common.dismiss')}
              onClose={keep.clearError}
              styles={{
                root: {
                  backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
                  border: '1px solid oklch(var(--mx-danger) / 0.45)',
                },
                title: { color: 'oklch(var(--mx-danger))' },
              }}
            >
              <Text size="sm">{keepRefusalMessage(keep.error, t)}</Text>
            </Alert>
          )}
        </Stack>
      ) : null}

      <VersionListView
        versions={versions}
        canKeep={canKeep}
        canRead={canRead}
        opened={reading}
        onOpen={(version) => {
          onRead({
            revisionNo: version.revisionNo,
            language: version.role === 'translation' ? version.languageCode : undefined,
          });
        }}
        onBack={() => {
          onRead(undefined);
        }}
      />
    </Stack>
  );
}

/**
 * What a refused keep says. The operation declares 401, 403 (`NOT_ARTICLE_OWNER`,
 * `AUTHOR_CAPABILITY_REQUIRED`, `ARTICLE_NOT_WRITABLE`), 404 and 429. The two 403s below are said
 * here because `describeProblem`'s sentence for the owner code is about a COMMENT and it has none
 * for the status code; the rest are its own.
 */
export function keepRefusalMessage(error: unknown, t: ArticlesT): string {
  if (hasErrorCode(error, 'ARTICLE_NOT_WRITABLE')) {
    return t('editor.history.notWritable');
  }
  if (hasErrorCode(error, 'NOT_ARTICLE_OWNER')) {
    return t('editor.history.notYours');
  }
  return describeProblem(error);
}

/** "Version 3", or "Version 3, Polish translation" for a translation row. */
function versionName(
  version: {
    readonly revisionNo: number;
    readonly role: string;
    readonly languageCode: string;
  },
  t: ArticlesT,
): string {
  return version.role === 'translation'
    ? t('editor.history.translationName', {
        number: version.revisionNo,
        language: sourceLanguageLabel(version.languageCode, [], t),
      })
    : t('editor.history.versionName', { number: version.revisionNo });
}

function VersionListView({
  versions,
  opened,
  onOpen,
  onBack,
  canKeep,
  canRead,
}: {
  readonly versions: AsyncResult<VersionList>;
  readonly canKeep: boolean;
  readonly canRead: boolean;
  readonly opened: OpenedVersion | undefined;
  readonly onOpen: (version: ArticleVersionSummary) => void;
  readonly onBack: () => void;
}) {
  const { t } = useTranslation('articles');
  if (versions.data === undefined) {
    return versions.status === 'error' ? (
      <ErrorState
        title={t('editor.history.didNotLoad')}
        error={versions.error}
        onRetry={versions.refetch}
      />
    ) : (
      <LoadingState label={t('editor.history.reading')} rows={2} />
    );
  }
  if (versions.data.items.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {canKeep ? t('editor.history.noneYetKeep') : t('editor.history.noneYet')}
      </Text>
    );
  }
  return (
    <Stack
      component="ol"
      gap="xs"
      aria-label={t('editor.history.listLabel')}
      style={{ listStyle: 'none', margin: 0, padding: 0 }}
    >
      {versions.data.items.map((version, _index, items) => {
        const name = versionName(version, t);
        const isOpen =
          opened !== undefined &&
          opened.revisionNo === version.revisionNo &&
          (version.role === 'translation'
            ? opened.language === version.languageCode
            : opened.language === undefined);
        return (
          <Group
            component="li"
            key={`${String(version.revisionNo)}:${version.role}:${version.languageCode}`}
            justify="space-between"
            align="flex-start"
            wrap="nowrap"
            gap="sm"
            aria-current={isOpen ? 'true' : undefined}
          >
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Text size="sm" fw={600}>
                {name}
              </Text>
              {isOpen ? (
                <Text size="xs" fw={700}>
                  {t('editor.history.readingNow')}
                </Text>
              ) : null}
              <Text size="xs" c="dimmed">
                {reasonWords(version, items, t)}, {formatInstant(version.createdAt)}
                {version.wordCount === undefined ? '' : ` · ${wordsOf(version.wordCount, t)}`}
              </Text>
              {version.title === undefined || version.title.length === 0 ? null : (
                <Text size="xs" lineClamp={1}>
                  {version.title}
                </Text>
              )}
            </Stack>
            {/* While it is read, the row's button is the way back; "Read" waits for any image
                still uploading, since the draft is put out of sight while a version is read. */}
            <Button
              size="compact-xs"
              variant={isOpen ? 'filled' : 'default'}
              aria-label={
                isOpen
                  ? t('editor.history.backFrom', { name })
                  : t('editor.history.readName', { name })
              }
              disabled={!isOpen && !canRead}
              style={{ flexShrink: 0 }}
              onClick={() => {
                if (isOpen) {
                  onBack();
                } else {
                  onOpen(version);
                }
              }}
            >
              {isOpen ? t('editor.history.back') : t('editor.history.read')}
            </Button>
          </Group>
        );
      })}
    </Stack>
  );
}

export interface VersionInColumnProps {
  readonly articleId: string;
  readonly version: OpenedVersion;
  /** "Back to your draft" in the editor, "Back to the current version" in the read view. */
  readonly backLabel: string;
  readonly onBack: () => void;
}

/**
 * ★ ONE STORED VERSION, IN THE ARTICLE'S OWN COLUMN (design 24, T20): a bar naming the version and
 * its date, with the way back, then the version as the read view draws an article. Nothing here
 * changes the draft, and there is no restore (this file's header). The bar takes the focus as the
 * version opens, which also brings the top of the article into view - on a phone the side column,
 * and so the list just pressed, sits below the article.
 */
export function VersionInColumn({ articleId, version, backLabel, onBack }: VersionInColumnProps) {
  const { t } = useTranslation('articles');
  const api = useEditorApi();
  const read = useAsync(
    useCallback(
      (signal: AbortSignal) =>
        api.getArticleVersion(
          articleId,
          version.revisionNo,
          version.language === undefined ? undefined : { language: version.language },
          signal,
        ),
      [api, articleId, version.revisionNo, version.language],
    ),
    `version:${articleId}:${String(version.revisionNo)}:${version.language ?? ''}`,
  );
  const name = versionName(
    {
      revisionNo: version.revisionNo,
      role: version.language === undefined ? 'source' : 'translation',
      languageCode: version.language ?? '',
    },
    t,
  );
  const bar = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    bar.current?.focus();
  }, []);
  const stored = read.data;

  return (
    <section
      className={editorClasses.canvas}
      aria-label={t('editor.history.readOnlyLabel', { name })}
    >
      <div className={editorClasses.versionBar}>
        <p ref={bar} tabIndex={-1} className={editorClasses.versionBarText}>
          {t('editor.history.youAreReading', { name })}
          {stored === undefined ? '' : ` · ${formatDay(stored.createdAt.slice(0, 10))}`}
        </p>
        <Button size="xs" radius="xl" variant="default" onClick={onBack}>
          {backLabel}
        </Button>
      </div>
      {stored === undefined ? (
        read.status === 'error' ? (
          <ErrorState
            title={t('editor.history.didNotOpen')}
            error={read.error}
            onRetry={read.refetch}
          />
        ) : (
          <LoadingState
            label={t('editor.history.opening', { number: version.revisionNo })}
            rows={3}
          />
        )
      ) : (
        // In the version's own language: a translation row is read as written.
        <div lang={stored.languageCode}>
          {stored.cover === null || stored.cover === undefined ? null : (
            <img
              className={classes.cover}
              src={emulatorImageUrl(stored.cover.url)}
              onError={servedUrlOnError(stored.cover.url)}
              alt=""
            />
          )}
          <h2 className={classes.title}>
            {stored.title.length === 0 ? t('common.untitled') : stored.title}
          </h2>
          {stored.subtitle === undefined || stored.subtitle.length === 0 ? null : (
            <p className={classes.standfirst}>{stored.subtitle}</p>
          )}
          <div className={classes.body}>
            {stored.blocks.map((block) => (
              <ReadOnlyBlock key={block.blockId} block={block} articleId={articleId} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/** A version's block as the read view draws one; an image not served here is named instead. */
function ReadOnlyBlock({ block, articleId }: { readonly block: Block; readonly articleId: string }) {
  const { t } = useTranslation('articles');
  switch (block.kind) {
    case 'text':
    case 'quote':
      return <ReadOnlyText block={block} />;
    case 'image':
      return block.url === undefined ? (
        <Text size="xs" c="dimmed">
          {block.imageAlt
            ? t('editor.history.imageWithAlt', { alt: block.imageAlt })
            : t('editor.history.image')}
        </Text>
      ) : (
        <figure className={classes.figure}>
          <img
            src={emulatorImageUrl(block.url)}
            onError={servedUrlOnError(block.url)}
            alt={block.imageAlt ?? ''}
          />
          {block.caption === null || block.caption === undefined ? null : (
            <figcaption className={classes.caption}>{block.caption}</figcaption>
          )}
        </figure>
      );
    case 'divider':
      return <hr className={classes.divider} />;
    case 'video':
      return <BlockVideoPlayer articleId={articleId} blockId={block.blockId} video={block.video} />;
    default:
      return null;
  }
}

/**
 * ★ WHAT MADE A VERSION, AS A TRANSLATION ROW SAYS IT (the owner's live walk of real cabinets,
 * 2026-10-01). Every translation row carries the reason `submit` - the database allows it no other
 * (`ck_av_translation_reason`, src/modules/articles/services/article-version.writer.ts) - so the
 * thirteen translations of an article brought over from the old apps all read "Submitted for review",
 * of a piece nobody sent anywhere. A translation is made FROM a source row (`sourceRevisionNo`), so
 * it says what that row says when the row came over from the old platform, and otherwise which
 * version it was translated from. A source row keeps its own reason. (Since design 15 what that row
 * says is "Original version".)
 */
function reasonWords(
  version: ArticleVersionSummary,
  items: readonly ArticleVersionSummary[],
  t: ArticlesT,
): string {
  if (version.role !== 'translation') {
    return reasonLabel(version.reason, t);
  }
  const from = version.sourceRevisionNo;
  if (from === null || from === undefined) {
    return t('editor.history.translated');
  }
  const source = items.find((item) => item.role === 'source' && item.revisionNo === from);
  return source?.reason === 'import'
    ? reasonLabel('import', t)
    : t('editor.history.translatedFrom', { number: from });
}

/** "1 word", "640 words" - a version's length, singular for one (the owner's look, 2026-10-05). */
function wordsOf(count: number, t: ArticlesT): string {
  return t('common.words', { count, value: String(count) });
}
